/**
 * Booking Step Context
 * 
 * Manages navigation between booking flow steps with validation.
 * 
 * Steps:
 * 0. Booking Time (time selection)
 * 1. Bay Selection (bay selection)
 * 2. Payment Details (confirmation)
 * 3. Finish Your Account (optional)
 * 
 * @module booking/context
 */

import { 
  createContext, 
  useState, 
  useEffect,
  useCallback, 
  useContext,
  type ReactNode 
} from 'react';
import { useBooking } from './useBooking';
import { usePromoCodeCapture, BOOKING_PROMO_KEY } from '~/features/promotions';

/**
 * Step IDs for type safety
 */
export const BOOKING_STEPS = {
  TIME_SELECTION: 0,
  PAYMENT_DETAILS: 1,
  FINISH_ACCOUNT: 2,
} as const;

export type BookingStepId = typeof BOOKING_STEPS[keyof typeof BOOKING_STEPS];

/**
 * Booking Step Context Value
 */
export interface BookingStepContextValue {
  /** Current step index (0-based) */
  currentStep: BookingStepId;
  /** Total number of steps */
  totalSteps: number;
  /** Navigate to a specific step (with validation) */
  goToStep: (step: BookingStepId) => boolean;
  /** Go to next step (with validation) */
  goToNextStep: () => boolean;
  /** Go to previous step */
  goToPreviousStep: () => void;
  /** Check if user can navigate to a specific step */
  canGoToStep: (step: BookingStepId) => boolean;
  /**
   * The booking's promo code — claimed from a `?promo=` booking link or typed
   * at checkout. `null` = none.
   *
   * Held by `usePromoCodeCapture` on the booking flow's OWN key
   * (`BOOKING_PROMO_KEY`), never the subscription funnel's: one shared key
   * would let a code captured for a membership leak into a booking quote.
   * Stashed in sessionStorage, so it survives a refresh and the WaiverGate
   * swapping this provider out mid-flow; the confirmation page forgets it.
   */
  promoCode: string | null;
  /**
   * Trimmed; `null` or blank clears (storage, state, and the link's `?promo=`
   * param, so a refresh cannot re-claim a code the member removed).
   */
  setPromoCode: (code: string | null) => void;
  /** Check if current step is valid */
  isCurrentStepValid: () => boolean;
}

/**
 * Booking Step Context
 */
export const BookingStepContext = createContext<BookingStepContextValue | null>(null);

/**
 * Props for BookingStepProvider
 */
interface BookingStepProviderProps {
  children: ReactNode;
  /** Initial step (defaults to TIME_SELECTION) */
  initialStep?: BookingStepId;
}

/**
 * Booking Step Provider
 * 
 * Manages step navigation with validation logic.
 * 
 * @example
 * ```tsx
 * <BookingStepProvider>
 *   <BookingFlowPage />
 * </BookingStepProvider>
 * ```
 */
export function BookingStepProvider({ 
  children, 
  initialStep = BOOKING_STEPS.TIME_SELECTION 
}: BookingStepProviderProps) {
  const [currentStep, setCurrentStepRaw] = useState<BookingStepId>(initialStep);
  const promo = usePromoCodeCapture(BOOKING_PROMO_KEY);
  const booking = useBooking();
  
  const totalSteps = 3;

  /** Set step and scroll to top so the user always starts at the top of the new step */
  const setCurrentStep = useCallback((step: BookingStepId) => {
    setCurrentStepRaw(step);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);
  
  // Listen for clear booking events to reset step to TIME_SELECTION
  useEffect(() => {
    const handleClearBooking = () => {
      if (import.meta.env.DEV) {
        console.log('[BookingStepContext] Received clear booking event, resetting to TIME_SELECTION');
      }
      setCurrentStep(BOOKING_STEPS.TIME_SELECTION);
    };
    
    window.addEventListener('butter:clearBooking', handleClearBooking);
    return () => window.removeEventListener('butter:clearBooking', handleClearBooking);
  }, [setCurrentStep]);
  
  /**
   * Validate if time selection is complete (date and time only)
   */
  const isTimeSelectionValid = useCallback((): boolean => {
    return !!(
      booking.selectedDate &&
      booking.selectedStartTime &&
      booking.selectedEndTime
    );
  }, [booking.selectedDate, booking.selectedStartTime, booking.selectedEndTime]);
  
  /**
   * Validate if bay selection is complete
   */
  const isBaySelectionValid = useCallback((): boolean => {
    return booking.selectedBayId !== null;
  }, [booking.selectedBayId]);
  
  /**
   * Validate if payment details are complete: a guest user, plus a payment token —
   * or the guest continued without one because the club collects at the counter.
   * Fully-covered members never reach this check — they book directly from step 0
   * (`BookingTimeStep.handleConfirmBooking`), so everyone who traverses step 1 owes
   * something, and needs a card unless it is due at the club.
   */
  const isPaymentValid = useCallback((): boolean => {
    const hasGuestUser = !!booking.guestUser;
    const hasPaymentToken = !!booking.paymentMethodToken;
    return hasGuestUser && (hasPaymentToken || booking.payAtClub);
  }, [booking.guestUser, booking.paymentMethodToken, booking.payAtClub]);
  
  /**
   * Check if user can navigate to a specific step
   */
  const canGoToStep = useCallback((step: BookingStepId): boolean => {
    switch (step) {
      case BOOKING_STEPS.TIME_SELECTION:
        // Can always go back to time selection
        return true;
        
      case BOOKING_STEPS.PAYMENT_DETAILS:
        // Need both valid time AND bay selection to proceed to payment
        return isTimeSelectionValid() && isBaySelectionValid();
        
      case BOOKING_STEPS.FINISH_ACCOUNT:
        // Need time, bay, and payment to proceed to account
        return isTimeSelectionValid() && isBaySelectionValid() && isPaymentValid();
        
      default:
        return false;
    }
  }, [isTimeSelectionValid, isBaySelectionValid, isPaymentValid]);
  
  /**
   * Check if current step is valid
   */
  const isCurrentStepValid = useCallback((): boolean => {
    if (import.meta.env.DEV) {
      console.log('[BookingStepContext] isCurrentStepValid checking step:', {
        currentStep,
        currentStepValue: currentStep,
        TIME_SELECTION: BOOKING_STEPS.TIME_SELECTION,
        PAYMENT_DETAILS: BOOKING_STEPS.PAYMENT_DETAILS,
        FINISH_ACCOUNT: BOOKING_STEPS.FINISH_ACCOUNT
      });
    }
    
    let result = false;
    
    switch (currentStep) {
      case BOOKING_STEPS.TIME_SELECTION:
        // Time AND bay selection must be valid to proceed
        result = isTimeSelectionValid() && isBaySelectionValid();
        if (import.meta.env.DEV) {
          console.log('[BookingStepContext] TIME_SELECTION step valid:', result, {
            timeValid: isTimeSelectionValid(),
            bayValid: isBaySelectionValid()
          });
        }
        break;
        
      case BOOKING_STEPS.PAYMENT_DETAILS:
        result = isPaymentValid();
        if (import.meta.env.DEV) {
          console.log('[BookingStepContext] PAYMENT_DETAILS step valid:', result);
        }
        break;
        
      case BOOKING_STEPS.FINISH_ACCOUNT:
        // Account step is always valid (optional fields)
        result = true;
        if (import.meta.env.DEV) {
          console.log('[BookingStepContext] FINISH_ACCOUNT step valid:', result);
        }
        break;
        
      default:
        if (import.meta.env.DEV) {
          console.warn('[BookingStepContext] Unknown step in isCurrentStepValid:', {
            currentStep,
            typeOfCurrentStep: typeof currentStep,
            currentStepStringified: JSON.stringify(currentStep)
          });
        }
        result = false;
    }
    
    if (import.meta.env.DEV) {
      console.log('[BookingStepContext] isCurrentStepValid result:', result, 'for step:', currentStep);
    }
    
    return result;
  }, [currentStep, isTimeSelectionValid, isBaySelectionValid, isPaymentValid]);
  
  /**
   * Navigate to a specific step
   * 
   * @returns true if navigation succeeded, false if blocked by validation
   */
  const goToStep = useCallback((step: BookingStepId): boolean => {
    if (step < 0 || step >= totalSteps) {
      console.warn(`[BookingStepContext] Invalid step: ${step}`);
      return false;
    }
    
    if (!canGoToStep(step)) {
      console.warn(`[BookingStepContext] Cannot navigate to step ${step} - validation failed`);
      return false;
    }
    
    if (import.meta.env.DEV) {
      console.log(`[BookingStepContext] Navigating to step ${step}`);
    }
    
    setCurrentStep(step);
    return true;
  }, [canGoToStep, totalSteps, setCurrentStep]);
  
  /**
   * Go to next step (with validation)
   */
  const goToNextStep = useCallback((): boolean => {
    if (import.meta.env.DEV) {
      console.log('[BookingStepContext] goToNextStep called', {
        currentStep,
        stepName: currentStep === 0 ? 'TIME_SELECTION' : currentStep === 1 ? 'PAYMENT_DETAILS' : 'FINISH_ACCOUNT',
        // Log complete booking state for debugging
        bookingState: {
          hasGuestUser: !!booking.guestUser,
          guestUserId: booking.guestUser?.id,
          hasPaymentToken: !!booking.paymentMethodToken,
          paymentToken: booking.paymentMethodToken?.substring(0, 20) + '...',
          paymentProvider: booking.paymentProvider,
          hasTimeSlot: !!booking.selectedStartTime,
          hasDate: !!booking.selectedDate
        }
      });
    }
    
    const isValid = isCurrentStepValid();
    
    if (import.meta.env.DEV) {
      console.log('[BookingStepContext] isCurrentStepValid returned:', isValid);
    }
    
    if (!isValid) {
      console.warn('[BookingStepContext] Current step is invalid, cannot proceed', {
        currentStep,
        stepName: currentStep === 0 ? 'TIME_SELECTION' : currentStep === 1 ? 'PAYMENT_DETAILS' : 'FINISH_ACCOUNT',
        validationResult: isValid
      });
      return false;
    }
    
    const nextStep = currentStep + 1;
    
    // Validate nextStep is within bounds before type assertion
    if (nextStep < 0 || nextStep >= totalSteps) {
      console.warn('[BookingStepContext] No next step available', { nextStep, totalSteps });
      return false;
    }
    
    if (import.meta.env.DEV) {
      console.log('[BookingStepContext] Attempting to navigate to step:', {
        nextStep,
        stepName: nextStep === 0 ? 'TIME_SELECTION' : nextStep === 1 ? 'PAYMENT_DETAILS' : 'FINISH_ACCOUNT'
      });
    }
    
    return goToStep(nextStep as BookingStepId);
  }, [currentStep, isCurrentStepValid, goToStep, totalSteps, booking.guestUser, booking.paymentMethodToken, booking.paymentProvider, booking.selectedStartTime, booking.selectedDate]);
  
  /**
   * Go to previous step (always allowed)
   */
  const goToPreviousStep = useCallback((): void => {
    if (currentStep > 0) {
      const prevStep = (currentStep - 1) as BookingStepId;
      setCurrentStep(prevStep);
    }
  }, [currentStep, setCurrentStep]);
  
  // The steps' null-means-clear setter over the hook's split pair. The hook
  // routes a blank string to its own clear, so only `null` needs mapping here.
  const { setPromoCode: capturePromoCode, clearPromoCode } = promo;
  const setPromoCode = useCallback(
    (code: string | null) => (code === null ? clearPromoCode() : capturePromoCode(code)),
    [capturePromoCode, clearPromoCode],
  );

  const value: BookingStepContextValue = {
    currentStep,
    totalSteps,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canGoToStep,
    isCurrentStepValid,
    promoCode: promo.promoCode,
    setPromoCode,
  };
  
  return (
    <BookingStepContext.Provider value={value}>
      {children}
    </BookingStepContext.Provider>
  );
}

/**
 * Hook to access booking step context
 * 
 * @throws Error if used outside BookingStepProvider
 */
export function useBookingStep(): BookingStepContextValue {
  const context = useContext(BookingStepContext);
  
  if (!context) {
    throw new Error('useBookingStep must be used within BookingStepProvider');
  }
  
  return context;
}
